import { asArray, asRecord, asString } from "../document/validate.ts";
import type { JsonValue } from "../document/types.ts";
import type { FieldGroupDefinition } from "./objects.ts";

/**
 * An object type's field groups in the game contract (P1.8-T21): written only when a type declares some, each group's keys
 * in one order, and read back by path. A preset's values are whole numbers, text and booleans, the values a field form
 * writes.
 */

/**
 * Field groups as plain data, keys in one order.
 *
 * @param groups The groups.
 * @returns The plain records.
 */
export function plainFieldGroups(groups: readonly FieldGroupDefinition[]): Record<string, unknown>[] {
  return groups.map((group) => ({ id: group.id, fields: [...group.fields], ...(group.presets === undefined ? {} : { presets: group.presets.map((preset) => ({ id: preset.id, values: { ...preset.values } })) }) }));
}

/**
 * Reads a preset's value.
 *
 * @param value The value.
 * @param path Path for messages.
 * @returns The value.
 * @throws Error naming the path when it is not text, a number or a boolean.
 */
function readValue(value: unknown, path: string): JsonValue {
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value;
  }
  throw new Error(`${path} must be text, a number or a boolean`);
}

/**
 * Reads an object type's field groups. Their fields and presets are checked by `validateObjectType`.
 *
 * @param value The value.
 * @param path Path for messages.
 * @returns The groups.
 * @throws Error naming the path when it is not a list of groups with an id, a list of field names and optional presets.
 */
export function readFieldGroups(value: unknown, path: string): FieldGroupDefinition[] {
  return asArray(value, path).map((item, i) => {
    const group = asRecord(item, `${path}[${i}]`);
    const presets = group["presets"] === undefined ? undefined : asArray(group["presets"], `${path}[${i}].presets`).map((entry, j) => {
      const preset = asRecord(entry, `${path}[${i}].presets[${j}]`);
      const values = asRecord(preset["values"], `${path}[${i}].presets[${j}].values`);
      return { id: asString(preset["id"], `${path}[${i}].presets[${j}].id`), values: Object.fromEntries(Object.entries(values).map(([field, v]) => [field, readValue(v, `${path}[${i}].presets[${j}].values.${field}`)])) };
    });
    return { id: asString(group["id"], `${path}[${i}].id`), fields: asArray(group["fields"], `${path}[${i}].fields`).map((name, j) => asString(name, `${path}[${i}].fields[${j}]`)), ...(presets === undefined ? {} : { presets }) };
  });
}
